import { CollectionIcon } from "@xola/icons";
import { Badge, Checkbox, ComboBox, Modal } from "@xola/ui-kit";
import clsx from "clsx";
import { sortBy } from "lodash";
import React, { useMemo, useState } from "react";
import { CollectionNode, CollectionNodeObject, MAX_COLLECTION_NESTING_DEPTH } from "types";
import { SearchInput } from "../../../Common/SearchInput";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { CollectionImage } from "./CollectionImage";
import { NoResults } from "@/components/Common/NoResults";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { getCollectionNestingDepth, getConstructedItemPath } from "@/models/CollectionNode";

interface ParentCollectionOption {
    value: string;
    label?: string;
    collection?: CollectionNode;
    isDisabled?: boolean;
}

const ParentCollectionOptionLabel = ({ option }: { option: ParentCollectionOption }) => (
    <div className="flex items-center">
        {option.collection ? (
            <CollectionImage collection={option.collection} size="tiny" className="mr-0" />
        ) : (
            // All Products case
            <div className="mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center">
                <CollectionIcon />
            </div>
        )}
        <span className="pl-2">{option.label}</span>
    </div>
);

// Function to build collection checkbox value options
const useCollectionOptions = (currentCollectionId?: ID, enableDepthCheck = true, includeAllProducts = true) => {
    const { data: parentCollections, isLoading: isLoadingParentCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        { object: CollectionNodeObject.GROUP_COLLECTION_NODE },
    );

    const options = useMemo(() => {
        const allProductsOption: ParentCollectionOption = {
            value: "",
            label: "All Products",
            isDisabled: false,
        };

        const sortedParents = sortBy(parentCollections, (c) => (c.breadcrumb ?? "").toLowerCase());

        const collectionOptions: ParentCollectionOption[] = sortedParents.map((collection) => {
            const nestingDepth = getCollectionNestingDepth(collection.path);
            const isCurrentCollection = currentCollectionId && collection.id === currentCollectionId;
            const depthExceeded = enableDepthCheck && nestingDepth >= MAX_COLLECTION_NESTING_DEPTH;
            const isDisabled = depthExceeded || !!isCurrentCollection;

            // Construct the path for the collection: parentPath/parentId
            const constructedPath = getConstructedItemPath(collection.path, collection.id);

            return {
                value: constructedPath,
                label: collection.breadcrumb ?? collection.name,
                collection,
                isDisabled: !!isDisabled,
            };
        });

        return includeAllProducts ? [allProductsOption, ...collectionOptions] : collectionOptions;
    }, [parentCollections, currentCollectionId, enableDepthCheck, includeAllProducts]);

    return { options, isLoading: isLoadingParentCollections };
};

// Helper function to get selected labels for display
const getSelectedLabels = (
    selectedCount: number,
    selectedPaths: string[],
    allOptions: ParentCollectionOption[],
): string => {
    if (selectedCount === 0) {
        return "No collections selected";
    }
    if (selectedCount === 1) {
        const option = allOptions.find((opt) => opt.value === selectedPaths[0]);
        return option?.label ?? "";
    }
    return `${selectedCount} collections selected`;
};

interface SingleParentCollectionSelectorProps {
    value: string;
    currentCollectionId?: ID;
    onChange: (path: string) => void;
    enableDepthCheck?: boolean; // Optional prop to enable/disable Max nesting depth check
}

// Single selection component
export const SingleParentCollectionSelector = ({
    value,
    currentCollectionId,
    onChange,
    enableDepthCheck = true,
}: SingleParentCollectionSelectorProps) => {
    const { options: parentCollectionOptions, isLoading: isLoadingParentCollections } = useCollectionOptions(
        currentCollectionId,
        enableDepthCheck,
    );

    const selectedParentOption = useMemo(() => {
        return parentCollectionOptions.find((opt) => opt.value === value) ?? parentCollectionOptions[0];
    }, [parentCollectionOptions, value]);

    const handleChange = (option: ParentCollectionOption) => {
        onChange(option?.value ?? "");
    };

    return (
        <ComboBox
            isDisabled={isLoadingParentCollections}
            options={parentCollectionOptions}
            value={selectedParentOption}
            formatOptionLabel={(option: ParentCollectionOption) => <ParentCollectionOptionLabel option={option} />}
            isOptionDisabled={(option: ParentCollectionOption) => option.isDisabled}
            classNames={{
                option: () => "no-translate",
                singleValue: () => "no-translate",
            }}
            onChange={handleChange}
        />
    );
};

interface MultipleParentCollectionSelectorProps {
    value: string[];
    currentCollectionId?: ID;
    isOpen?: boolean;
    onChange: (paths: string[]) => void;
    onOpenChange?: (open: boolean) => void;
    enableDepthCheck?: boolean;
}

export const MultipleParentCollectionSelector = ({
    value,
    currentCollectionId,
    isOpen = false,
    onChange,
    onOpenChange,
    enableDepthCheck = true,
}: MultipleParentCollectionSelectorProps) => {
    const { options: allCollectionOptions, isLoading: isLoadingParentCollections } = useCollectionOptions(
        currentCollectionId,
        enableDepthCheck,
        false,
    );

    const selectedCount = value.length;

    const handleApply = (selectedPaths: string[]) => {
        onChange(selectedPaths);
        onOpenChange?.(false);
    };

    const handleOpen = () => {
        onOpenChange?.(true);
    };

    const handleClose = () => {
        onOpenChange?.(false);
    };

    const selectedLabels = useMemo(
        () => getSelectedLabels(selectedCount, value, allCollectionOptions),
        [selectedCount, value, allCollectionOptions],
    );

    return (
        <>
            <button
                type="button"
                className="flex w-full items-center justify-between rounded border border-gray-light bg-white px-3 py-2 text-left hover:border-gray"
                disabled={isLoadingParentCollections}
                onClick={handleOpen}
            >
                <span className="no-translate">{selectedLabels}</span>
                <Badge className="ml-2">{selectedCount}</Badge>
            </button>

            <Modal isOpen={isOpen} size="huge" onClose={handleClose}>
                <Modal.Header>Select Collections</Modal.Header>
                <MultipleCollectionModalBody
                    initialValue={value}
                    currentCollectionId={currentCollectionId}
                    enableDepthCheck={enableDepthCheck}
                    onApply={handleApply}
                    onClose={handleClose}
                />
            </Modal>
        </>
    );
};

const MultipleCollectionModalBody = ({
    initialValue,
    currentCollectionId,
    onApply,
    onClose,
    enableDepthCheck = true,
}: {
    initialValue: string[];
    currentCollectionId?: ID;
    onApply: (paths: string[]) => void;
    onClose: () => void;
    enableDepthCheck?: boolean;
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPaths, setSelectedPaths] = useState<string[]>(initialValue);

    const { options: allCollectionOptions } = useCollectionOptions(currentCollectionId, enableDepthCheck, false);

    // Filter collections based on search
    const filteredCollections = useMemo(() => {
        if (!searchTerm) {
            return allCollectionOptions;
        }
        const term = searchTerm.toLowerCase().trim();
        return allCollectionOptions.filter((opt) => (opt.label ?? "").toLowerCase().includes(term));
    }, [allCollectionOptions, searchTerm]);

    const selectedCount = selectedPaths.length;
    const isAllSelected = selectedPaths.length > 0 && selectedPaths.length === allCollectionOptions.length;

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedPaths([]);
        } else {
            setSelectedPaths(allCollectionOptions.map((opt) => opt.value));
        }
    };

    const handleToggle = (path: string) => {
        if (selectedPaths.includes(path)) {
            setSelectedPaths(selectedPaths.filter((selectedPath) => selectedPath !== path));
        } else {
            setSelectedPaths([...selectedPaths, path]);
        }
    };

    const handleApply = () => {
        onApply(selectedPaths);
    };

    return (
        <>
            <Modal.Body>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Checkbox
                            checked={isAllSelected}
                            label="Select all"
                            classNames={{
                                label: "font-bold",
                            }}
                            onChange={handleSelectAll}
                        />
                        <Badge>{selectedCount} Selected</Badge>
                    </div>

                    <SearchInput value={searchTerm} onChange={setSearchTerm} />

                    <div className="h-96 divide-y divide-gray-light overflow-y-auto rounded border border-gray-light">
                        {filteredCollections.length === 0 ? (
                            <NoResults>No results found</NoResults>
                        ) : (
                            filteredCollections.map((option) => (
                                <Checkbox
                                    key={option.value}
                                    checked={selectedPaths.includes(option.value)}
                                    disabled={option.isDisabled}
                                    label={<ParentCollectionOptionLabel option={option} />}
                                    className={clsx(
                                        "px-3 py-2 hover:cursor-pointer",
                                        selectedPaths.includes(option.value)
                                            ? "bg-blue-lighter"
                                            : "hover:bg-gray-hover",
                                    )}
                                    classNames={{ checkbox: "!border !border-gray" }}
                                    onChange={() => handleToggle(option.value)}
                                />
                            ))
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <button
                    type="button"
                    className="rounded bg-blue px-4 py-2 text-white hover:bg-blue-dark"
                    onClick={handleApply}
                >
                    Apply
                </button>
            </Modal.Footer>
        </>
    );
};
